/* eslint-disable @typescript-eslint/naming-convention */

import {
  createAdEvent,
  createComponentClickedEvent,
  createExternalLinkClickedEvent,
  createScreenViewedEvent,
  createSubmittedEvent,
  track,
} from "./base"

import { type LoanProfileType } from "@/generated/graphql"
import { type BranchParams } from "react-native-branch"
import type { JsonMap } from "@/analytics/client"

export const registerViewed = (variant?: string) => {
  track(
    createScreenViewedEvent({
      current_screen: "Register",
      screen_variant: variant || "default",
    })
  )
}

export const confirmRegistrationViewed = () => {
  track(createScreenViewedEvent({ current_screen: "Confirm Registration" }))
}

export const googleAuthRegistrationSubmitted = ({
  email,
  userExists,
}: {
  email: string
  userExists: boolean
}) => {
  track(
    createSubmittedEvent({
      object_name: "Registration",
      current_screen: "Register",
      to_screen: "Confirm Registration",
      component_type: "google_auth_button",
      component_text: "Sign In with Google",
      submitted_values: {
        email,
        user_exists: userExists,
      },
    })
  )
}

export const appleAuthRegistrationSubmitted = ({
  email,
  userExists,
}: {
  email: string
  userExists: boolean
}) => {
  track(
    createSubmittedEvent({
      object_name: "Registration",
      current_screen: "Register",
      to_screen: "Confirm Registration",
      component_type: "apple_auth_button",
      component_text: "Sign In with Apple",
      submitted_values: {
        email,
        user_exists: userExists,
      },
    })
  )
}

export const emailRegistrationSubmitted = ({ email }: { email: string }) => {
  track(
    createSubmittedEvent({
      object_name: "Registration",
      current_screen: "Register",
      to_screen: "Confirm Registration",
      component_type: "button",
      component_text: "Sign Up",
      submitted_values: {
        email,
      },
    })
  )
}

export const termsAndConditionsClicked = ({
  action,
}: {
  action: "select" | "deselect"
}) => {
  track(
    createComponentClickedEvent({
      current_screen: "Confirm Registration",
      component_type: "check_box",
      component_function: "accept terms",
      component_text:
        "I agree to Backflip’s Terms of Use and acknowledge the Privacy Policy.",
      action_taken: action,
    })
  )
}

export const termsOfUseClicked = ({ url }: { url: string }) => {
  track(
    createExternalLinkClickedEvent({
      current_screen: "Confirm Registration",
      to_screen: "Ext:Terms of Use",
      component_type: "text_link",
      link_text: "Terms of Use",
      link_url: url,
    })
  )
}

export const privacyPolicyClicked = ({ url }: { url: string }) => {
  track(
    createExternalLinkClickedEvent({
      current_screen: "Confirm Registration",
      to_screen: "Ext:Privacy Policy",
      component_type: "text_link",
      link_text: "Privacy Policy",
      link_url: url,
    })
  )
}

export const registrationConfirmSubmitted = ({
  firstName,
  lastName,
  phoneNumber,
  preQual,
}: {
  firstName: string
  lastName: string
  phoneNumber?: string | null
  preQual?: Partial<LoanProfileType> | null
}) => {
  track(
    createSubmittedEvent({
      object_name: "Registration Confirmation",
      current_screen: "Confirm Registration",
      to_screen: "Home",
      component_type: "button",
      component_text: "Sign Up",
      submitted_values: {
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        is_pre_qual_flow: !!preQual?.propertiesFlipped,
        properties_flipped: preQual?.propertiesFlipped,
        rentals_owned: preQual?.rentalsOwned,
        cash_available: preQual?.cashAvailable,
        credit_score: preQual?.creditScore,
        borrower: preQual?.borrower,
      },
    })
  )
}

export const userCapturedByAd = ({
  utmMedium,
  utmSource,
  utmCampaign,
  utmContent,
  utmTerm,
}: {
  utmMedium?: string | null
  utmSource?: string | null
  utmCampaign?: string | null
  utmContent?: string | null
  utmTerm?: string | null
}) => {
  track(
    createAdEvent({
      event_name: "User Captured By Ad",
      utm_medium: utmMedium || null,
      utm_source: utmSource || null,
      utm_campaign: utmCampaign || null,
      utm_content: utmContent || null,
      utm_term: utmTerm || null,
    })
  )
}

export const branchOpenEvent = ({ params }: { params: BranchParams }) => {
  track(["Branch Open", JSON.parse(JSON.stringify(params)) as JsonMap])
}
